import { computed, ref, watch } from "vue";

export const LIST_SORT_OPTIONS = [
    { value: "recent", label: "Most Recently Updated" },
    { value: "created", label: "Newest" },
    { value: "name-asc", label: "Name A–Z" },
    { value: "name-desc", label: "Name Z–A" },
];

const PER_PAGE = 9;

/**
 * Client-side sorting + pagination for the profile list grids (GH-820).
 * The controller returns every visible list at once, so slicing here keeps
 * the sort dropdown and pager instant without extra requests.
 *
 * @param {import("vue").ComputedRef<Array<object>>} lists
 */
export function useListGrid(lists) {
    const sort = ref("recent");
    const page = ref(1);

    const sorted = computed(() => {
        const copy = [...lists.value];

        switch (sort.value) {
            case "created":
                return copy.sort((a, b) =>
                    b.created_at.localeCompare(a.created_at),
                );
            case "name-asc":
                return copy.sort((a, b) => a.name.localeCompare(b.name));
            case "name-desc":
                return copy.sort((a, b) => b.name.localeCompare(a.name));
            default:
                // Most recent: latest activity first.
                return copy.sort((a, b) =>
                    b.updated_at.localeCompare(a.updated_at),
                );
        }
    });

    const pages = computed(() =>
        Math.max(1, Math.ceil(sorted.value.length / PER_PAGE)),
    );

    const paged = computed(() =>
        sorted.value.slice((page.value - 1) * PER_PAGE, page.value * PER_PAGE),
    );

    watch(sort, () => (page.value = 1));

    return { sort, page, pages, paged };
}
